import React, { useEffect, useState } from 'react'
import apiClient from '../../../utils/apiclient'
import { useNavigate } from 'react-router-dom'

const RiderDashboard = (route) => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingRiders, setLoadingRiders] = useState(false)
  const [riders, setRiders] = useState([])
  const [batchData, setBatchData] = useState([])
  const [allOrdersCount, setAllOrdersCount] = useState(0)
  const [activeOrdersCount, setActiveOrdersCount] = useState(0)
  const [filters, setFilters] = useState(() => {
    const now = new Date()
    const endDate = now.toISOString().slice(0, 10)
    const start = new Date(now)
    start.setDate(start.getDate() - 30)
    const startDate = start.toISOString().slice(0, 10)
    return {
      riderId: '',
      startDate,
      endDate,
    }
  })
  const [activeFilters, setActiveFilters] = useState(filters)

  useEffect(() => {
    fetchDashboard()
  }, [route.route, activeFilters])

  useEffect(() => {
    fetchRiders()
  }, [])

  const fetchRiders = async () => {
    try {
      setLoadingRiders(true)
      const response = await apiClient.get('/AllRiders?page=0&size=100')
      const riderList = Array.isArray(response?.data?.content) ? response.data.content : []
      setRiders(riderList)
    } catch (e) {
      console.error('Failed to fetch riders', e)
      setRiders([])
    } finally {
      setLoadingRiders(false)
    }
  }

  const fetchDashboard = async () => {
    try {
      setLoading(true)

      const body = {
        startDate: activeFilters.startDate,
        endDate: activeFilters.endDate,
      }

      if (activeFilters.riderId.trim()) {
        body.riderId = Number(activeFilters.riderId.trim())
      }

      const response = await apiClient.post(route.route, body)

      const responseData = response.data || {}
      const payload = responseData.allOrders || responseData.content || []
      const batches = Array.isArray(responseData.orderBatches) ? responseData.orderBatches : []
      const allCount = responseData.allOrdersCount ?? payload.length ?? 0
      const activeCount = responseData.activeOrdersCount ?? payload.filter(order => {
        const status = order.orderStatus?.toLowerCase() || ''
        return status === 'on the way' || status === 'rider is assigned'
      }).length ?? 0

      setData(Array.isArray(payload) ? payload : [])
      setBatchData(batches)
      setAllOrdersCount(allCount)
      setActiveOrdersCount(activeCount)
    } catch (e) {
      console.error('Failed to fetch rider dashboard', e)
      setData([])
      setBatchData([])
      setAllOrdersCount(0)
      setActiveOrdersCount(0)
    } finally {
      setLoading(false)
    }
  }

  const filteredData = Array.isArray(data)
    ? data.filter((item) => {
        if (search.trim() === '') return true

        const term = search.toLowerCase()

        return (
          item.orderId?.toString().includes(term) ||
          item.publicId?.toLowerCase().includes(term) ||
          item.customerNumber?.toString().includes(term) ||
          item.customerName?.toLowerCase().includes(term) ||
          item.restaurantName?.toLowerCase().includes(term) ||
          item.riderName?.toLowerCase().includes(term)
        )
      })
    : []

  const handleFilterUpdate = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const applyFilters = () => {
    setActiveFilters(filters)
  }

  return (
    <div className="w-full">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-3 flex-shrink-0">
        <div>
          <h2 className="text-base md:text-xl font-semibold text-gray-800 whitespace-nowrap">Rider Dashboard</h2>
          <p className="text-sm text-gray-500 mt-1">
            Use Rider ID and dates to fetch dashboard orders, then filter within results.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-2 w-full sm:grid-cols-2 lg:grid-cols-4 md:w-auto">
          <select
            className="border-2 py-1.5 px-3 md:py-2 md:px-4 text-sm md:text-base rounded-lg outline-none focus:border-blue-500"
            value={filters.riderId}
            onChange={(e) => handleFilterUpdate('riderId', e.target.value)}
            disabled={loadingRiders}
          >
            <option value="">{loadingRiders ? 'Loading riders...' : 'Select Rider'}</option>
            {riders.map((rider) => (
              <option key={rider.id} value={rider.id}>
                {rider.name || `Rider ${rider.id}`}
                {rider.number ? ` - ${rider.number}` : ''}
              </option>
            ))}
          </select>
          <input
            type="date"
            className="border-2 py-1.5 px-3 md:py-2 md:px-4 text-sm md:text-base rounded-lg outline-none focus:border-blue-500"
            value={filters.startDate}
            onChange={(e) => handleFilterUpdate('startDate', e.target.value)}
          />
          <input
            type="date"
            className="border-2 py-1.5 px-3 md:py-2 md:px-4 text-sm md:text-base rounded-lg outline-none focus:border-blue-500"
            value={filters.endDate}
            onChange={(e) => handleFilterUpdate('endDate', e.target.value)}
          />
          <button
            onClick={applyFilters}
            className="bg-blue-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-blue-700 transition-colors"
          >
            Load
          </button>
        </div>
      </div>

      <div className="flex justify-between items-center mb-3 flex-wrap gap-3">
        <div className="text-sm text-gray-600">
          Showing {filteredData.length} of {data.length} fetched orders
        </div>
        <input
          className="border-2 py-1.5 px-3 md:py-2 md:px-4 text-sm md:text-base rounded-lg outline-none focus:border-blue-500 w-full md:w-72"
          placeholder="Filter by order ID, public ID, customer, restaurant, or rider"
          onChange={(e) => setSearch(e.target.value)}
          value={search}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-2 gap-3 mb-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
          <div className="text-xs md:text-sm text-gray-600 font-medium">All Orders Count</div>
          <div className="text-lg md:text-2xl font-bold text-blue-600">{allOrdersCount}</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 md:p-4">
          <div className="text-xs md:text-sm text-gray-600 font-medium">Active Orders Count</div>
          <div className="text-lg md:text-2xl font-bold text-green-600">{activeOrdersCount}</div>
        </div>
      </div>

      <div className="w-full">
        {batchData.length > 0 && (
          <div className="mb-4">
            <div className="text-sm font-semibold text-gray-700 mb-2">Batch Summary</div>
            <div className="border border-gray-200 rounded-lg overflow-y-auto overflow-x-auto max-h-40">
              <table className="min-w-[520px] md:min-w-full bg-white border-collapse">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="px-2 md:px-3 py-2 text-left text-[11px] md:text-sm">Batch ID</th>
                    <th className="px-2 md:px-3 py-2 text-left text-[11px] md:text-sm">Created At</th>
                    <th className="px-2 md:px-3 py-2 text-left text-[11px] md:text-sm">Updated At</th>
                    <th className="px-2 md:px-3 py-2 text-left text-[11px] md:text-sm">Order Count</th>
                    <th className="px-2 md:px-3 py-2 text-left text-[11px] md:text-sm">Delivery Status</th>
                  </tr>
                </thead>
                <tbody>
                  {batchData.map((batch, index) => (
                    <tr key={batch.id || index} className="border-t border-gray-200">
                      <td className="px-2 md:px-3 py-2 text-[11px] md:text-sm">{batch.id ?? '-'}</td>
                      <td className="px-2 md:px-3 py-2 text-[11px] md:text-sm">{batch.createdAt ?? '-'}</td>
                      <td className="px-2 md:px-3 py-2 text-[11px] md:text-sm">{batch.updatedAt ?? '-'}</td>
                      <td className="px-2 md:px-3 py-2 text-[11px] md:text-sm">{batch.ordercount ?? '-'}</td>
                      <td className="px-2 md:px-3 py-2 text-[11px] md:text-sm">{batch.status ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="w-full pb-4 relative mb-4">
          <div className="text-sm font-semibold text-gray-700 mb-2">Orders</div>
          
          {loading && (
            <div className="text-center py-4 text-gray-500">Loading orders...</div>
          )}

          <div className="overflow-y-auto overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-max w-full bg-white border-collapse">
              <thead className="bg-gray-800 text-white shadow-md">
              <tr>
                <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm">Order ID</th>
                <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm">Public ID</th>
                <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm">Customer Number</th>
                <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm">Customer Name</th>
                <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm">Restaurant</th>
                <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm">Order Status</th>
                <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm">Freebie Delivered</th>
                <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm">Order Date</th>
                <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm">Start Time</th>
                <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm">End Time</th>
                <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm">Order Price</th>
                <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm">Meal Plan</th>
                <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm">Meal Name</th>
                <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm">Order Type</th>
                <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm">Rider Name</th>
                <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm">Rider Number</th>
                <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm">Created At</th>
                <th className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm">Updated At</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 && !loading ? (
                <tr>
                  <td colSpan="18" className="text-center py-8 text-gray-500">
                    No orders found for the selected filters.
                  </td>
                </tr>
              ) : (
                filteredData.map((order, index) => (
                  <tr key={order.orderId || index} className="items-center justify-center border border-gray-300">
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">{order.orderId}</td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">{order.publicId}</td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">{order.customerNumber}</td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">{order.customerName}</td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center hover:text-blue-900 hover:cursor-pointer hover:underline" onClick={() => navigate(`/restaurant/${order.restaurantId || order.restaurantid || ''}`)}>
                      {order.restaurantName}
                    </td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">{order.orderStatus}</td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">{order.freebieDelivered ? 'Yes' : 'No'}</td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">{order.orderDate}</td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">{order.startTime}</td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">{order.endTime}</td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">{order.orderPrice}</td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">{order.mealPlan}</td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">{order.mealname}</td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">{order.orderType}</td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">{order.riderName}</td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">{order.riderNumber}</td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center whitespace-nowrap">{order.createdAt ? order.createdAt.split('T')[1]?.split('.')[0] : '-'}</td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center whitespace-nowrap">{order.updatedAt ? order.updatedAt.split('T')[1]?.split('.')[0] : '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
            </table>

          </div>
        </div>
      </div>

    </div>
  )
}

export default RiderDashboard
